// LA IMPORTACION TAMBIEN ARMA FAMILIAS.
//
// Antes solo creaba fichas: los 197 jugadores de RAIDERS cayeron en "sin familia" y se
// arreglaron uno por uno en Fichas por completar. Las reglas son las del alta, sin
// una sola nueva:
//
//   - hermanos con el mismo responsable (mismo nombre Y mismo telefono) van juntos;
//   - una persona que ya existe en el club (mismo telefono Y mismo nombre) se reusa
//     tal cual, y los importados se suman a la familia donde ya responde;
//   - nada se adivina: el telefono solo no junta a nadie (papa y mama lo comparten),
//     y un menor sin tutor queda sin familia -- Fichas por completar lo lista.
//
// Todo aqui es puro: entran filas ya normalizadas por csvPlayers.validateRow, salen
// grupos y etiquetas. Quien escribe es el servicio (pagos.crearFamiliaConResponsable).

import { isMinor } from './age'
import { normalizarTexto, mismoTexto, soloDigitos, resolverResponsable } from './responsableResolver'

const limpio = (t) => String(t || '').trim()

// Quien responde por esta fila, con la misma regla que responsableParaCuenta():
// lo que dice la fila; si no dice nada y el jugador es mayor, el mismo; si es menor,
// nadie. Sin fecha de nacimiento no se asume mayor.
const responsableDeFila = (fila, asOf) => {
  const tutor = limpio(fila.tutor_nombre)
  if (tutor) {
    return {
      nombre: tutor,
      telefono: soloDigitos(fila.tutor_telefono),
      email: limpio(fila.email_tutor),
      esElJugador: mismoTexto(tutor, fila.nombre_completo),
    }
  }
  if (isMinor(fila.fecha_nacimiento, asOf) === false) {
    return { nombre: limpio(fila.nombre_completo), telefono: soloDigitos(fila.telefono_jugador), email: '', esElJugador: true }
  }
  return null
}

// Agrupa las filas por responsable. Devuelve { grupos, sinResponsable }.
// Cada grupo: { clave, nombre, telefono, email, esElJugador, filas, existente: null }.
export const agruparPorResponsable = (filas, asOf = new Date()) => {
  const porClave = new Map()
  const sinResponsable = []
  for (const fila of filas) {
    const r = responsableDeFila(fila, asOf)
    if (!r) { sinResponsable.push(fila); continue }
    const clave = `${normalizarTexto(r.nombre)}|${r.telefono}`
    let g = porClave.get(clave)
    if (!g) {
      g = { clave, nombre: r.nombre, telefono: r.telefono, email: r.email, esElJugador: r.esElJugador, filas: [], existente: null }
      porClave.set(clave, g)
    }
    if (!g.email && r.email) g.email = r.email
    g.filas.push(fila)
  }
  return { grupos: Array.from(porClave.values()), sinResponsable }
}

// Con los candidatos que devolvio buscarPersonaPorTelefono(), decide si el grupo reusa
// a una persona que ya existe. Misma pregunta 1 del alta: solo con nombre Y telefono.
// La familia se hereda solo si esa persona responde por UNA; con dos seria adivinar.
export const ligarConExistentes = (grupo, candidatos = []) => {
  const decision = resolverResponsable({
    candidatosPorTelefono: candidatos,
    nombreCapturado: grupo.nombre,
    telefonoCapturado: grupo.telefono,
  })
  if (decision.accion !== 'usar') return { ...grupo, existente: null }
  const persona = candidatos.find((c) => c?.id === decision.personaId)
  const familia = decision.familiaSugerida
    ? (persona?.familia_personas || []).map((v) => v?.familias).find((f) => f?.id === decision.familiaSugerida)
    : null
  return {
    ...grupo,
    existente: { personaId: decision.personaId, familiaId: familia?.id || null, familiaNombre: familia?.nombre || null },
  }
}

// Lo que va a pasar con la familia de una fila, en palabras, para la vista previa.
export const etiquetaFamiliaImportada = (grupo) => {
  if (!grupo) return { texto: 'Sin responsable', tono: 'aviso' }
  if (grupo.esElJugador) return { texto: 'Responde por sí mismo', tono: 'normal' }
  if (grupo.existente?.familiaId) {
    return { texto: `${grupo.nombre} · se suma a ${grupo.existente.familiaNombre}`, tono: 'normal' }
  }
  if (grupo.existente?.personaId) {
    return { texto: `${grupo.nombre} · ya registrada, familia nueva`, tono: 'normal' }
  }
  const n = (grupo.filas || []).length
  return {
    texto: n > 1 ? `${grupo.nombre} · nueva familia (${n} del archivo)` : `${grupo.nombre} · nueva familia`,
    tono: 'normal',
  }
}

// Para el reporte final.
export const resumenFamilias = ({ grupos = [], sinResponsable = [] } = {}) => ({
  familiasNuevas: grupos.filter((g) => !g.existente?.familiaId).length,
  sumadosAExistentes: grupos.filter((g) => g.existente?.familiaId).reduce((n, g) => n + g.filas.length, 0),
  sinResponsable: sinResponsable.length,
})
